import australiaStatesGeoJson from "./data/australia-states.json";
import { STATES_LIST } from "Constants/constant";

export const AUSTRALIA_MAP_VIEWBOX = "0 0 800 700";

export const ALL_AUSTRALIAN_STATE_CODES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

export const GEOGRAPHY_MAP_METRICS = {
  TENANTS: "tenants",
  COLLECTED: "collected_all_time_cents",
  MRR: "estimated_mrr_cents",
};

export const GEOGRAPHY_MAP_METRIC_OPTIONS = [
  { id: GEOGRAPHY_MAP_METRICS.TENANTS, label: "Tenants" },
  { id: GEOGRAPHY_MAP_METRICS.COLLECTED, label: "Collected" },
  { id: GEOGRAPHY_MAP_METRICS.MRR, label: "Est. MRR" },
];

const MAP_BOUNDS = {
  minLng: 112.5,
  maxLng: 154.5,
  minLat: -44.5,
  maxLat: -9.5,
};

const MAP_PADDING = 24;

/** GeoJSON feature name → state code */
export const GEOJSON_STATE_NAME_TO_CODE = {
  "New South Wales": "NSW",
  Victoria: "VIC",
  Queensland: "QLD",
  "South Australia": "SA",
  "Western Australia": "WA",
  Tasmania: "TAS",
  "Northern Territory": "NT",
  "Australian Capital Territory": "ACT",
};

/** Approximate label anchors for state codes on the SVG map */
export const STATE_LABEL_ANCHORS = {
  WA: { x: 170, y: 380 },
  NT: { x: 430, y: 280 },
  SA: { x: 430, y: 470 },
  QLD: { x: 620, y: 300 },
  NSW: { x: 680, y: 470 },
  VIC: { x: 620, y: 560 },
  TAS: { x: 680, y: 640 },
  ACT: { x: 710, y: 520 },
};

const HEAT_EMPTY = "#E5E7EB";
const HEAT_MIN = "#C7D2FE";
const HEAT_MAX = "#4338CA";
const REVENUE_HEAT_MAX = "#059669";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const projectPoint = ([lng, lat]) => {
  const innerWidth = 800 - MAP_PADDING * 2;
  const innerHeight = 700 - MAP_PADDING * 2;
  const x =
    MAP_PADDING +
    ((lng - MAP_BOUNDS.minLng) / (MAP_BOUNDS.maxLng - MAP_BOUNDS.minLng)) * innerWidth;
  const y =
    MAP_PADDING +
    ((MAP_BOUNDS.maxLat - lat) / (MAP_BOUNDS.maxLat - MAP_BOUNDS.minLat)) * innerHeight;

  return [x, y];
};

const ringToPath = (ring) =>
  ring
    .map(([lng, lat], index) => {
      const [x, y] = projectPoint([lng, lat]);
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ") + " Z";

const geometryToPath = (geometry) => {
  if (!geometry) {
    return "";
  }

  if (geometry.type === "Polygon") {
    return geometry.coordinates.map(ringToPath).join(" ");
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates
      .map((polygon) => polygon.map(ringToPath).join(" "))
      .join(" ");
  }

  return "";
};

export const getStateLabel = (code) =>
  STATES_LIST.find((state) => state.label === code)?.value ||
  GEOJSON_STATE_NAME_TO_CODE[code] ||
  code;

export const getStateHeatColor = (count, maxCount) => {
  if (!count) {
    return HEAT_EMPTY;
  }

  if (!maxCount) {
    return HEAT_MIN;
  }

  const ratio = clamp(count / maxCount, 0, 1);
  const mix = (start, end) => Math.round(start + (end - start) * ratio);

  const red = mix(0xc7, 0x43);
  const green = mix(0xd2, 0x38);
  const blue = mix(0xfe, 0xca);

  return `rgb(${red}, ${green}, ${blue})`;
};

export const getRevenueHeatColor = (amountCents, maxAmountCents) => {
  if (!amountCents) {
    return HEAT_EMPTY;
  }

  if (!maxAmountCents) {
    return "#A7F3D0";
  }

  const ratio = clamp(amountCents / maxAmountCents, 0, 1);
  const mix = (start, end) => Math.round(start + (end - start) * ratio);

  const red = mix(0xe5, 0x05);
  const green = mix(0xe7, 0x96);
  const blue = mix(0xeb, 0x69);

  return `rgb(${red}, ${green}, ${blue})`;
};

export const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${Number(value).toFixed(1)}%`;
};

export const indexTenantGeographyByCode = (geography) => {
  const index = {};

  (geography?.states || []).forEach((state) => {
    if (state?.code) {
      index[state.code] = state;
    }
  });

  return index;
};

export const getTenantGeographyMaxCount = (geography) =>
  Math.max(0, ...(geography?.states || []).map((state) => state.total || 0));

export const getGeographyMetricValue = (state = {}, metric = GEOGRAPHY_MAP_METRICS.TENANTS) => {
  if (metric === GEOGRAPHY_MAP_METRICS.TENANTS) {
    return state.total || 0;
  }

  return state[metric] || 0;
};

export const getGeographyMetricMax = (geography, metric = GEOGRAPHY_MAP_METRICS.TENANTS) =>
  Math.max(
    0,
    ...(geography?.states || []).map((state) => getGeographyMetricValue(state, metric))
  );

export const buildCompleteStateBreakdownRows = (geography) => {
  const index = indexTenantGeographyByCode(geography);

  return ALL_AUSTRALIAN_STATE_CODES.map(
    (code) =>
      index[code] || {
        code,
        name: getStateLabel(code),
        total: 0,
        active: 0,
        suspended: 0,
        archived: 0,
        on_trial: 0,
        paying_tenants: 0,
        estimated_mrr_cents: 0,
        collected_all_time_cents: 0,
        collected_last_30_days_cents: 0,
        outstanding_cents: 0,
        tenant_share_pct: 0,
        revenue_share_pct: 0,
      }
  );
};

export const buildAustraliaMapFeatures = (
  geography,
  { metric = GEOGRAPHY_MAP_METRICS.TENANTS, revenueAvailable = false } = {}
) => {
  const countsByCode = indexTenantGeographyByCode(geography);
  const isTenantMetric = metric === GEOGRAPHY_MAP_METRICS.TENANTS;
  const maxValue = getGeographyMetricMax(geography, metric);

  return (australiaStatesGeoJson.features || []).map((feature) => {
    const name = feature?.properties?.STATE_NAME;
    const code = GEOJSON_STATE_NAME_TO_CODE[name] || null;
    const stats = code ? countsByCode[code] : null;
    const metricValue = stats ? getGeographyMetricValue(stats, metric) : 0;
    const count = stats?.total || 0;
    const fill =
      isTenantMetric || !revenueAvailable
        ? getStateHeatColor(metricValue, maxValue)
        : getRevenueHeatColor(metricValue, maxValue);

    return {
      code,
      name: stats?.name || name,
      path: geometryToPath(feature.geometry),
      count,
      metricValue,
      active: stats?.active || 0,
      suspended: stats?.suspended || 0,
      archived: stats?.archived || 0,
      onTrial: stats?.on_trial || 0,
      payingTenants: stats?.paying_tenants || 0,
      estimatedMrrCents: stats?.estimated_mrr_cents || 0,
      collectedAllTimeCents: stats?.collected_all_time_cents || 0,
      collectedLast30DaysCents: stats?.collected_last_30_days_cents || 0,
      outstandingCents: stats?.outstanding_cents || 0,
      tenantSharePct: stats?.tenant_share_pct || 0,
      revenueSharePct: stats?.revenue_share_pct || 0,
      fill,
      label: STATE_LABEL_ANCHORS[code] || null,
    };
  });
};

export const buildTenantGeographyLegendSteps = (maxCount, { revenue = false } = {}) => {
  if (!maxCount) {
    return [{ label: revenue ? "$0" : "0 tenants", color: HEAT_EMPTY }];
  }

  if (revenue) {
    return [
      { label: "$0", color: HEAT_EMPTY },
      { label: "Low", color: getRevenueHeatColor(maxCount * 0.25, maxCount) },
      { label: "Medium", color: getRevenueHeatColor(maxCount * 0.6, maxCount) },
      { label: "High", color: getRevenueHeatColor(maxCount, maxCount) },
    ];
  }

  const step = Math.max(1, Math.ceil(maxCount / 4));

  return [
    { label: "0", color: HEAT_EMPTY },
    { label: `1–${step}`, color: getStateHeatColor(1, maxCount) },
    { label: `${step + 1}–${step * 2}`, color: getStateHeatColor(step * 2, maxCount) },
    { label: `${step * 2 + 1}+`, color: getStateHeatColor(maxCount, maxCount) },
  ];
};

export const buildTenantsListPathForState = (stateCode) => {
  if (!stateCode) {
    return "/tenants";
  }

  const params = new URLSearchParams({
    state: stateCode,
    status: "all",
  });

  return `/tenants?${params.toString()}`;
};

export const sortTenantGeographyStates = (states = []) =>
  [...states].sort((left, right) => {
    if ((right.total || 0) !== (left.total || 0)) {
      return (right.total || 0) - (left.total || 0);
    }

    return (left.code || "").localeCompare(right.code || "");
  });

export const formatTenantGeographySummary = (geography) => {
  const mapped = geography?.mapped_tenants ?? 0;
  const unmapped = geography?.unmapped_tenants ?? 0;
  const total = geography?.total_tenants ?? mapped + unmapped;
  const parts = [];

  parts.push(`${total} tenant${total === 1 ? "" : "s"} across Australia`);

  if (unmapped) {
    parts.push(`${unmapped} unmapped`);
  }

  if (geography?.revenue_available && geography?.revenue_totals) {
    parts.push(
      `${geography.revenue_totals.paying_tenants || 0} paying · collected & MRR by state below`
    );
  }

  return parts.join(" · ");
};
